/**
 * @fileoverview Organism: the form that adds a purchase.
 * @module components/organisms/PurchaseForm
 */

import { useState, type FormEvent } from "react"

import { Button, Input, Select } from "@/components/atoms"
import { formatCents } from "@/lib/domain/amortisation"
import { PREDEFINED_CATEGORIES } from "@/lib/domain/categories"
import type { PurchaseDraft } from "@/types/types"

import "./PurchaseForm.css"

/** Props accepted by {@link PurchaseForm}. */
export interface PurchaseFormProps {
	/** Called with a valid draft when the form is submitted. */
	readonly onSubmit: (draft: PurchaseDraft) => void
	/** Pre-fills the fields for editing; omit for a blank "add" form. */
	readonly initialValue?: PurchaseDraft
	/** Submit button label. Defaults to "Suivre cet achat". */
	readonly submitLabel?: string
}

/** Field-level validation messages, keyed by field name. */
type FieldErrors = Partial<Record<"label" | "price" | "target", string>>

/** Editable form state: everything is a string until it is parsed. */
type FormValues = Record<"label" | "price" | "target" | "category", string>

/** Blank form state. */
const EMPTY: FormValues = { label: "", price: "", target: "", category: "" }

/**
 * Converts a draft into the strings the form fields edit.
 *
 * @param draft - The draft to pre-fill from, or undefined for a blank form.
 * @returns Form values seeded from the draft.
 */
function toFormValues(draft: PurchaseDraft | undefined): FormValues {
	if (draft === undefined) return EMPTY

	return {
		label: draft.label,
		price: String(draft.priceCents / 100),
		target: String(draft.targetUses),
		category: draft.category ?? "",
	}
}

/**
 * Parses a euro amount typed by the user into cents.
 *
 * @param value - Raw input value, e.g. `"79,90"`.
 * @returns The amount in cents, or null when unparsable.
 */
function parseEuros(value: string): number | null {
	const normalised = value.replace(",", ".").trim()
	if (normalised === "") return null

	const euros = Number(normalised)
	if (!Number.isFinite(euros) || euros < 0) return null

	return Math.round(euros * 100)
}

/**
 * Form adding a purchase to the collection.
 *
 * @param props - Submit handler receiving a validated draft, plus optional
 *   pre-filled values when the form is used to edit rather than add.
 * @returns The rendered form.
 */
export function PurchaseForm({
	onSubmit,
	initialValue,
	submitLabel = "Suivre cet achat",
}: PurchaseFormProps) {
	const [values, setValues] = useState(() => toFormValues(initialValue))
	const [errors, setErrors] = useState<FieldErrors>({})

	const priceCents = parseEuros(values.price)
	const targetUses = Number(values.target)
	const hasTarget = values.target !== "" && Number.isFinite(targetUses)

	// A purchase saved before this list existed, or imported, may carry a
	// category outside it: keep it selectable instead of dropping it silently.
	const customCategory =
		values.category !== "" &&
		!PREDEFINED_CATEGORIES.includes(values.category)
			? values.category
			: null

	/**
	 * Validates the form and hands a draft up on success.
	 *
	 * @param event - The submit event.
	 */
	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()

		const nextErrors: FieldErrors = {}

		if (values.label.trim() === "") {
			nextErrors.label = "Donnez un nom à cet achat."
		}
		if (priceCents === null) {
			nextErrors.price = "Indiquez un prix valide."
		}
		if (!hasTarget || targetUses < 1) {
			nextErrors.target = "Au moins une utilisation visée."
		}

		setErrors(nextErrors)

		if (Object.keys(nextErrors).length > 0 || priceCents === null) return

		onSubmit({
			label: values.label,
			priceCents,
			targetUses: Math.round(targetUses),
			...(values.category.trim() === ""
				? {}
				: { category: values.category }),
		})

		// Editing keeps the modal open on the submitted values; only a fresh
		// "add" form clears itself for the next entry.
		if (initialValue === undefined) setValues(EMPTY)
	}

	// Live feedback on what the purchase must cost per use to be worth it.
	const targetCostPerUse =
		priceCents !== null && hasTarget && targetUses >= 1
			? Math.round(priceCents / targetUses)
			: null

	return (
		<form className="purchase-form" onSubmit={handleSubmit} noValidate>
			<div className="purchase-form__grid">
				<Input
					label="Objet"
					value={values.label}
					placeholder="Polo"
					onChange={event => {
						setValues({ ...values, label: event.target.value })
					}}
					{...(errors.label === undefined
						? {}
						: { error: errors.label })}
				/>
				<Input
					label="Prix (€)"
					value={values.price}
					inputMode="decimal"
					placeholder="80"
					onChange={event => {
						setValues({ ...values, price: event.target.value })
					}}
					{...(errors.price === undefined
						? {}
						: { error: errors.price })}
				/>
				<Input
					label="Utilisations visées"
					value={values.target}
					inputMode="numeric"
					placeholder="80"
					onChange={event => {
						setValues({ ...values, target: event.target.value })
					}}
					{...(errors.target === undefined
						? {}
						: { error: errors.target })}
				/>
			</div>

			<Select
				label="Catégorie"
				value={values.category}
				onChange={event => {
					setValues({ ...values, category: event.target.value })
				}}
			>
				<option value="">Sans catégorie</option>
				{customCategory !== null && (
					<option value={customCategory}>{customCategory}</option>
				)}
				{PREDEFINED_CATEGORIES.map(category => (
					<option key={category} value={category}>
						{category}
					</option>
				))}
			</Select>

			{targetCostPerUse !== null && (
				<p className="purchase-form__hint">
					Objectif : {formatCents(targetCostPerUse)} par utilisation.
				</p>
			)}

			<div>
				<Button type="submit">{submitLabel}</Button>
			</div>
		</form>
	)
}
